/**
 * The Query tools that had no panel: Identify, Attribute table, Buffer.
 *
 * All three work against the layers their config scopes them to, and all three
 * read their remaining settings — tolerance, page size, distance and unit —
 * from the same place. An empty layer selection means every layer; see
 * `scopedLayers`.
 */

import { useEffect, useState } from 'react';
import { Loader2, Plus, Table2, Trash2 } from 'lucide-react';
import ToolPanelHeader, { ToolActions } from '../ToolPanel';
import { cfg, scopedLayers, type ToolContext } from './types';
import {
  EmptyHint,
  Field,
  FieldRow,
  Notice,
  ResultHeader,
  ResultRow,
  ScrollList,
  btnClass,
  inputClass,
  LayerSelect,
} from './shared';
import { getLayerDisplayName } from '../../../layers/capabilities';
import { centroidOf, labelOf, loadFeatures, loadFeaturesDetailed } from './featureLoad';
import type { LayerRenderer, RendererCategory } from '../../../types';
import turfBuffer from '@turf/buffer';
import turfUnion from '@turf/union';
import turfDifference from '@turf/difference';

/* ────────────────────────────── Identify ────────────────────────────────── */

const EARTH_RADIUS_M = 6378137;

/** Metres per screen pixel at a latitude and zoom, for the click tolerance. */
function metresPerPixel(lat: number, zoom: number) {
  return (2 * Math.PI * EARTH_RADIUS_M * Math.cos((lat * Math.PI) / 180)) / (256 * 2 ** zoom);
}

function distanceMetres(a: [number, number], b: [number, number]) {
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = toRad(b[1] - a[1]);
  const dLng = toRad(b[0] - a[0]);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a[1])) * Math.cos(toRad(b[1])) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
}

export function IdentifyPanel({ ctx }: { ctx: ToolContext }) {
  const tolerancePx = Math.max(1, cfg<number>(ctx.config, 'tolerance', 5));
  const targets = scopedLayers(ctx.config, ctx.layers).filter((layer) => layer.checked);
  const [hits, setHits] = useState<{ layer: string; features: GeoJSON.Feature[] }[]>([]);
  const [busy, setBusy] = useState(false);

  const point = ctx.pointer;
  const pointKey = point ? `${point.lat.toFixed(6)},${point.lng.toFixed(6)}` : '';

  /* Re-runs on every map click. The tolerance is in PIXELS, the way Esri asks
     for it, so it is converted to ground metres at the current zoom — the same
     5px means 60m at city zoom and under a metre when zoomed right in. */
  useEffect(() => {
    if (!point || targets.length === 0) {
      setHits([]);
      return undefined;
    }
    let live = true;
    setBusy(true);
    const radius = metresPerPixel(point.lat, ctx.mapZoom) * tolerancePx;

    const run = async () => {
      const found: { layer: string; features: GeoJSON.Feature[] }[] = [];
      for (const layer of targets) {
        const features = await loadFeatures(layer, ctx, { limit: 1000 });
        const near = features.filter((feature) => {
          const centre = centroidOf(feature.geometry);
          return centre ? distanceMetres(centre, [point.lng, point.lat]) <= radius : false;
        });
        if (near.length > 0) found.push({ layer: getLayerDisplayName(layer), features: near });
      }
      if (live) {
        setHits(found);
        setBusy(false);
      }
    };
    void run();
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pointKey, tolerancePx, ctx.mapZoom, targets.length]);

  return (
    <>
      <ToolPanelHeader
        title="Identify"
        description="What sits under the point you click."
        onClose={ctx.onClose}
      />

      {targets.length === 0 ? (
        <EmptyHint>
          No layers are switched on for this tool. Tick a layer in the Layers tab first.
        </EmptyHint>
      ) : !point ? (
        <EmptyHint>
          Click the map. Anything within {tolerancePx}&nbsp;pixels of the click is listed here.
        </EmptyHint>
      ) : busy ? (
        <p className="flex items-center gap-2 text-[12px] text-slate-500">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Looking under the click…
        </p>
      ) : hits.length === 0 ? (
        <EmptyHint>
          Nothing within {tolerancePx}&nbsp;pixels of that point. Raise the tolerance in the Tool
          Library, or click closer to a feature.
        </EmptyHint>
      ) : (
        <div className="space-y-2">
          {hits.map((hit) => (
            <div key={hit.layer} className="space-y-1">
              <ResultHeader label={hit.layer} count={hit.features.length} />
              <ScrollList max={160}>
                {hit.features.slice(0, 25).map((feature, index) => {
                  const centre = centroidOf(feature.geometry);
                  return (
                    <ResultRow
                      key={`${hit.layer}-${index}`}
                      title={labelOf(feature, index)}
                      detail={centre ? `${centre[1].toFixed(5)}, ${centre[0].toFixed(5)}` : undefined}
                      onClick={
                        centre
                          ? () => ctx.onGoToLocation([centre[1], centre[0]], Math.max(ctx.mapZoom, 17))
                          : undefined
                      }
                    />
                  );
                })}
              </ScrollList>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

/* ────────────────────────── Attribute table ─────────────────────────────── */

export function AttributeTablePanel({ ctx }: { ctx: ToolContext }) {
  const targets = scopedLayers(ctx.config, ctx.layers);

  return (
    <>
      <ToolPanelHeader
        title="Attribute table"
        description="Open a layer's records in a floating table."
        onClose={ctx.onClose}
      />
      {targets.length === 0 ? (
        <EmptyHint>This map has no layers yet. Add one from the Layers panel.</EmptyHint>
      ) : (
        <ScrollList max={300}>
          {targets.map((layer) => (
            <ResultRow
              key={layer.id}
              title={getLayerDisplayName(layer)}
              detail={layer.checked ? 'On the map' : 'Switched off'}
              onClick={() => ctx.onOpenAttributeTable?.(layer)}
              action={<Table2 className="h-3.5 w-3.5 flex-shrink-0 text-slate-400" />}
            />
          ))}
        </ScrollList>
      )}
      {/* One window at a time is a deliberate constraint of the table itself,
          so say it here rather than letting it surprise someone. */}
      <p className="text-[10.5px] leading-snug text-slate-400">
        Opening another layer swaps the table's contents — there is one window, not one per layer.
      </p>
    </>
  );
}

/* ─────────────────────────────── Buffer ─────────────────────────────────── */

const UNIT_METRES: Record<string, number> = {
  m: 1,
  km: 1000,
  ft: 0.3048,
  mi: 1609.344,
};

/**
 * Buffering ANY geometry — point, line, or polygon — is `@turf/buffer`'s job
 * now, not hand-rolled per-segment capsules.
 *
 * WHAT USED TO BE HERE, AND WHY IT'S GONE
 * The previous approach built a line's buffer one SEGMENT at a time, each as
 * its own capsule (a rectangle the segment's length, capped with a FULL
 * semicircle at both ends). That is only correct at a genuine sharp corner —
 * on an ordinary stretch of road, most vertices are there to follow a gentle
 * curve or are just noise in how the source data was digitised, and a full
 * radius-R bulge at every single one of them is not what a line buffer is
 * supposed to look like. Dense street data (a block-by-block road network can
 * have a vertex every few metres) turned this into a visible "string of
 * beads" along the whole line — every vertex its own round bump — instead of
 * a smooth, constant-width corridor. It also had no polygon-union step, so
 * two adjacent source rows (a road service commonly splits one street into
 * one row per block) still left a seam where their buffers only touched, not
 * blended.
 *
 * `@turf/buffer` solves the actual geometry problem this app was
 * approximating by hand: real offset curves with correct joins (round by
 * default, matching a desktop GIS's own default buffer style), so a straight
 * run of vertices stays a straight-edged corridor and only a genuine corner
 * gets a rounded bulge. `@turf/union` then merges every source feature's own
 * buffer into ONE seamless shape, so multiple disjoint rows read as one
 * buffer, not several overlapping ones. See `run()` below for how the two are used together.
 */

/** Metres-per-degree at a latitude, for the ground-distance checks below
 *  (what falls inside the finished buffer) — the buffer SHAPE itself no
 *  longer needs this; `@turf/buffer` does its own projection internally. */
function metresPerDegreeAt(latDeg: number): { lat: number; lng: number } {
  const metresPerDegLat = EARTH_RADIUS_M * (Math.PI / 180);
  return { lat: metresPerDegLat, lng: metresPerDegLat * Math.max(Math.cos((latDeg * Math.PI) / 180), 1e-6) };
}

/** Lon/lat → flat metres relative to `origin`. Valid near `origin`. */
function toLocalMetres(point: [number, number], origin: [number, number]): [number, number] {
  const scale = metresPerDegreeAt(origin[1]);
  return [(point[0] - origin[0]) * scale.lng, (point[1] - origin[1]) * scale.lat];
}

/** Shortest distance from a point to a single segment, in ground metres. */
function pointToSegmentMetres(point: [number, number], a: [number, number], b: [number, number]): number {
  const origin = point;
  const A = toLocalMetres(a, origin);
  const B = toLocalMetres(b, origin);
  const abx = B[0] - A[0];
  const aby = B[1] - A[1];
  const lengthSq = abx * abx + aby * aby;
  const t = lengthSq > 1e-9 ? Math.max(0, Math.min(1, (-A[0] * abx - A[1] * aby) / lengthSq)) : 0;
  const closestX = A[0] + t * abx;
  const closestY = A[1] + t * aby;
  return Math.hypot(closestX, closestY);
}

function minDistanceAlongLine(point: [number, number], coords: number[][]): number {
  let min = Infinity;
  for (let index = 0; index < coords.length - 1; index += 1) {
    min = Math.min(min, pointToSegmentMetres(point, coords[index] as [number, number], coords[index + 1] as [number, number]));
  }
  return min;
}

/**
 * How far a point sits from a geometry — not just from its centroid, so
 * "what falls inside the buffer" is asking the same question the buffer ring
 * itself now answers geometrically for a line or a polygon's boundary.
 */
function minDistanceToGeometryMetres(point: [number, number], geometry: GeoJSON.Geometry | null | undefined): number {
  if (!geometry) return Infinity;
  switch (geometry.type) {
    case 'Point':
      return distanceMetres(point, geometry.coordinates as [number, number]);
    case 'MultiPoint':
      return Math.min(...geometry.coordinates.map((c) => distanceMetres(point, c as [number, number])));
    case 'LineString':
      return minDistanceAlongLine(point, geometry.coordinates);
    case 'MultiLineString':
      return Math.min(...geometry.coordinates.map((line) => minDistanceAlongLine(point, line)));
    case 'Polygon':
      return Math.min(...geometry.coordinates.map((ring) => minDistanceAlongLine(point, ring)));
    case 'MultiPolygon':
      return Math.min(
        ...geometry.coordinates.flatMap((polygon) => polygon.map((ring) => minDistanceAlongLine(point, ring))),
      );
    default:
      return Infinity;
  }
}

/**
 * One feature's-worth of buffer work: buffer every source geometry at
 * `radiusKm`, then union them into a single (Multi)Polygon. Shared by the
 * single-ring path and, once per ring distance, by the multi-ring path
 * below — the "buffer everything, then merge the seams away" idea from the
 * single-ring fix doesn't change just because it now runs more than once.
 */
async function unionBufferAt(
  geometries: GeoJSON.Geometry[],
  radiusKm: number,
): Promise<GeoJSON.Feature<GeoJSON.Polygon | GeoJSON.MultiPolygon> | null> {
  const buffered: GeoJSON.Feature<GeoJSON.Polygon | GeoJSON.MultiPolygon>[] = [];
  for (const geometry of geometries) {
    const result = turfBuffer({ type: 'Feature', properties: {}, geometry }, radiusKm, {
      units: 'kilometers',
      steps: 16,
    });
    if (result) buffered.push(result);
  }
  if (buffered.length === 0) return null;
  if (buffered.length === 1) return buffered[0];

  try {
    const merged = turfUnion({ type: 'FeatureCollection', features: buffered });
    if (merged) return merged;
  } catch {
    // falls through to the un-merged shape below
  }
  return {
    type: 'Feature',
    properties: {},
    geometry: {
      type: 'MultiPolygon',
      coordinates: buffered.flatMap((feature) =>
        feature.geometry.type === 'Polygon' ? [feature.geometry.coordinates] : feature.geometry.coordinates,
      ),
    },
  };
}

/** A ring distance as both the number the person typed and its metres
 *  equivalent — kept together so a band's label can show "100–200 m" in
 *  the unit they actually entered, rather than a value recomputed back out
 *  of metres and picking up float noise on the way. */
interface RingDistance {
  value: number;
  metres: number;
}

interface RingBand {
  geometry: GeoJSON.Geometry;
  label: string;
  fromM: number;
  toM: number;
}

/**
 * Multiple ring buffer — the same idea Esri's own "Multiple Ring Buffer"
 * tool and QGIS's "Multi-ring buffer" offer: not several independent
 * buffers stacked on top of each other (which would just make the inner
 * rings invisible under the outer ones' fill), but DONUT bands — each ring
 * is the area between it and the ring before it, so they tile edge to edge
 * with no overlap and no gap. `@turf/difference` cuts each band out of the
 * next disk up (see `unionBufferAt` for how each full disk is itself built).
 */
async function buildRingBands(geometries: GeoJSON.Geometry[], rings: RingDistance[]): Promise<RingBand[]> {
  const sorted = [...rings].sort((a, b) => a.metres - b.metres);
  const disks = await Promise.all(sorted.map((ring) => unionBufferAt(geometries, ring.metres / 1000)));

  const bands: RingBand[] = [];
  for (let index = 0; index < sorted.length; index += 1) {
    const outer = disks[index];
    if (!outer) continue;
    const from = index === 0 ? 0 : sorted[index - 1].metres;
    const label = index === 0 ? `0–${sorted[0].value}` : `${sorted[index - 1].value}–${sorted[index].value}`;

    if (index === 0) {
      bands.push({ geometry: outer.geometry, label, fromM: from, toM: sorted[index].metres });
      continue;
    }

    const inner = disks[index - 1];
    let bandGeometry = outer.geometry;
    if (inner) {
      try {
        const diff = turfDifference({ type: 'FeatureCollection', features: [outer, inner] });
        if (diff) bandGeometry = diff.geometry;
      } catch {
        // keep the un-subtracted outer disk rather than dropping the band
      }
    }
    bands.push({ geometry: bandGeometry, label, fromM: from, toM: sorted[index].metres });
  }
  return bands;
}

const RING_PALETTE = [
  '#2563EB', '#0D9488', '#D97706', '#DC2626', '#7C3AED', '#0891B2', '#65A30D', '#DB2777',
];

export function BufferPanel({ ctx }: { ctx: ToolContext }) {
  const allowed = scopedLayers(ctx.config, ctx.layers);
  const listResults = cfg<boolean>(ctx.config, 'listResults', true);

  const [layerId, setLayerId] = useState(allowed[0]?.id ?? '');
  /** All of the layer's features (the tool's original behaviour), or only
   *  whichever ones are currently selected — on the shared selection
   *  channel the Select/Select By Location tools, the Query tool and the
   *  attribute table all publish to, via `getSelection`. The same choice
   *  ClipPanel already offers for its own two source layers. */
  const [sourceMode, setSourceMode] = useState<'all' | 'selected'>('all');
  const [distance, setDistance] = useState(String(cfg<number>(ctx.config, 'distance', 500)));
  const [unit, setUnit] = useState(cfg<string>(ctx.config, 'unit', 'm'));
  /** Single ring (the tool's original behaviour) vs. multiple concentric
   *  donut bands — Esri's own "Multiple Ring Buffer" and QGIS's "Multi-ring
   *  buffer" tools offer the same choice. Works for every geometry type,
   *  the same way the single-ring path already does — a ring band is just
   *  `unionBufferAt` run once per distance and the results differenced, and
   *  none of that cares whether the source was a point, a line, or a
   *  polygon's boundary. */
  const [mode, setMode] = useState<'single' | 'multi'>('single');
  const [ringDistances, setRingDistances] = useState<string[]>(['100', '200', '300']);
  const [busy, setBusy] = useState(false);
  const [summary, setSummary] = useState<{
    features: number;
    radiusM: number;
    layerId: string | null;
    mode: 'single' | 'multi';
    ringCount?: number;
  } | null>(null);
  const [inside, setInside] = useState<{ layer: string; count: number }[]>([]);
  const [error, setError] = useState<string | null>(null);
  /** Set when the source layer had more features than were actually
   *  buffered — see the comment at the load call below. Separate from
   *  `error`: a truncated buffer is still a real, usable result, not a
   *  failure, so it can sit alongside a normal summary rather than replacing it. */
  const [truncatedAt, setTruncatedAt] = useState<number | null>(null);

  const source = allowed.find((layer) => layer.id === layerId);

  const updateRingDistance = (index: number, value: string) => {
    setRingDistances((current) => current.map((entry, position) => (position === index ? value : entry)));
  };
  const addRing = () => setRingDistances((current) => [...current, '']);
  const removeRing = (index: number) =>
    setRingDistances((current) => current.filter((_, position) => position !== index));

  const run = async () => {
    if (!source) return;

    /* Parsed and validated up front, before anything async, so a typo in
       either mode's distance field fails fast with the same message shape
       the single-ring path always gave. */
    let singleRadiusM = 0;
    let sortedRings: RingDistance[] = [];
    if (mode === 'single') {
      singleRadiusM = Number(distance) * (UNIT_METRES[unit] ?? 1);
      if (!Number.isFinite(singleRadiusM) || singleRadiusM <= 0) {
        setError('Enter a distance greater than zero.');
        return;
      }
    } else {
      const parsed = ringDistances
        .map((value) => Number(value))
        .filter((value) => Number.isFinite(value) && value > 0)
        .map((value) => ({ value, metres: value * (UNIT_METRES[unit] ?? 1) }));
      // De-duplicated by METRES, not by the typed value — "0.1" km and "100" m
      // are the same ring twice, and differencing a disk from an identical
      // disk would produce an empty band.
      sortedRings = Array.from(new Map(parsed.map((ring) => [ring.metres, ring])).values()).sort(
        (a, b) => a.metres - b.metres,
      );
      if (sortedRings.length === 0) {
        setError('Enter at least one ring distance greater than zero.');
        return;
      }
    }

    setError(null);
    setTruncatedAt(null);
    setBusy(true);
    setInside([]);
    try {
      let features: GeoJSON.Feature[];
      if (sourceMode === 'selected') {
        // The shared selection channel, narrowed to this source layer's own
        // rows — the same filter ClipPanel already applies for its own
        // "from selected features" option.
        features = (ctx.getSelection?.() ?? [])
          .filter((row) => row.layerId === source.id && row.geometry)
          .map((row) => ({
            type: 'Feature' as const,
            properties: row.properties ?? {},
            geometry: row.geometry as GeoJSON.Geometry,
          }));
      } else {
        /* 5,000, not the 1,000 a plain `loadFeatures` call defaults to — a
           street network split into one row per block routinely has several
           thousand rows, and silently buffering only the first 1,000 of them
           read as "some streets just don't have a buffer" with nothing telling
           anyone why. `loadFeaturesDetailed` (not `loadFeatures`) is used
           specifically so a service capping the answer lower than that — most
           ArcGIS services do, at 1,000 or 2,000 regardless what is asked for —
           is still detected and reported below, rather than swallowed. */
        const loaded = await loadFeaturesDetailed(source, ctx, { limit: 5000 });
        features = loaded.features;
        if (loaded.truncated) setTruncatedAt(loaded.features.length);
      }
      const geometries = features
        .map((feature) => feature.geometry)
        .filter((geometry): geometry is GeoJSON.Geometry => Boolean(geometry));

      if (geometries.length === 0) {
        setSummary(null);
        setError(
          sourceMode === 'selected'
            ? 'No features are currently selected on that layer.'
            : 'That layer returned no geometry to buffer.',
        );
        return;
      }

      const outerRadiusM = mode === 'single' ? singleRadiusM : sortedRings[sortedRings.length - 1].metres;

      let layerFeatures: GeoJSON.Feature[];
      let layerName: string;
      let renderer: LayerRenderer;

      if (mode === 'single') {
        /*
         * Each source geometry is buffered on its own — a point, a line, or a
         * polygon's boundary, `@turf/buffer` handles all of them correctly,
         * with real offset curves and round joins only where the line
         * actually turns, not a bulge at every vertex regardless of angle.
         * Every source feature's own buffer then gets UNIONED into one shape
         * — a road most GIS services store as many separate short LineString
         * rows (one per block, split at each junction) would otherwise read
         * as beads on a string right where one row's buffer ended and the
         * next one's began, even with each individual buffer correctly
         * shaped: two adjacent, merely-touching polygons still show a seam.
         */
        const merged = await unionBufferAt(geometries, singleRadiusM / 1000);
        if (!merged) {
          setSummary(null);
          setError('That layer returned no geometry to buffer.');
          return;
        }
        layerFeatures = [
          {
            type: 'Feature',
            properties: { source: getLayerDisplayName(source), radius_m: Math.round(singleRadiusM) },
            geometry: merged.geometry,
          },
        ];
        layerName = `Buffer — ${getLayerDisplayName(source)} (${distance} ${unit})`;
        renderer = {
          type: 'single',
          color: '#2563EB',
          // A real outline, safely: `merged.geometry` above is ONE union'd
          // (Multi)Polygon with a genuine outer boundary, not a pile of
          // overlapping per-segment rings — so stroking it draws exactly
          // that boundary, not an internal seam at every vertex.
          fillOpacity: 0.14,
          outlineColor: '#1D4ED8',
          outlineWidth: 1.5,
          pointSize: 8,
        };
      } else {
        // Multiple concentric donut bands — see buildRingBands's own doc
        // comment for why each is a DIFFERENCE, not just another buffer.
        const bands = await buildRingBands(geometries, sortedRings);
        if (bands.length === 0) {
          setSummary(null);
          setError('That layer returned no geometry to buffer.');
          return;
        }
        layerFeatures = bands.map((band, index) => ({
          type: 'Feature',
          properties: {
            source: getLayerDisplayName(source),
            ring: `${band.label} ${unit}`,
            ring_index: index,
            from_m: Math.round(band.fromM),
            to_m: Math.round(band.toM),
          },
          geometry: band.geometry,
        }));
        layerName = `Multi-ring buffer — ${getLayerDisplayName(source)}`;
        const categories: RendererCategory[] = layerFeatures.map((feature, index) => ({
          value: String((feature.properties as Record<string, unknown>).ring),
          color: RING_PALETTE[index % RING_PALETTE.length],
        }));
        renderer = {
          type: 'unique',
          field: 'ring',
          color: RING_PALETTE[0],
          defaultColor: '#94A3B8',
          categories,
          fillOpacity: 0.35,
          outlineColor: '#1D4ED8',
          outlineWidth: 1,
          pointSize: 8,
        };
      }

      const rings: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: layerFeatures };

      /* Lands in the TOC as a real layer rather than a fleeting overlay, so it
         survives closing the panel and can be renamed, restyled, toggled and
         removed like anything else. It belongs to this person only. */
      const newLayerId = ctx.onCreateTempLayer?.(layerName, rings, 'Buffer', renderer);
      // Reported per SOURCE feature, not per polygon primitive underneath —
      // one line can legitimately need several capsules to draw its buffer,
      // and "12 rings" would read as something having gone wrong rather than
      // as one buffered road.
      setSummary({
        features: geometries.length,
        radiusM: outerRadiusM,
        layerId: newLayerId ?? null,
        mode,
        ringCount: mode === 'multi' ? sortedRings.length : undefined,
      });

      if (listResults) {
        const others = ctx.layers.filter((layer) => layer.id !== source.id && layer.checked);
        const counts: { layer: string; count: number }[] = [];
        for (const other of others) {
          const otherFeatures = await loadFeatures(other, ctx, { limit: 1000 });
          const count = otherFeatures.filter((feature) => {
            const centre = centroidOf(feature.geometry);
            if (!centre) return false;
            // Distance to the geometry itself, not to its centroid — a point
            // 20 m from the SIDE of a long line is inside the buffer of it
            // even though it may be hundreds of metres from that line's
            // midpoint. Tested against the OUTERMOST ring in multi-ring mode.
            return geometries.some((geometry) => minDistanceToGeometryMetres(centre, geometry) <= outerRadiusM);
          }).length;
          if (count > 0) counts.push({ layer: getLayerDisplayName(other), count });
        }
        setInside(counts);
      }
    } finally {
      setBusy(false);
    }
  };

  /* Clears the PANEL, not the map. The buffer is a layer now: removing it is
     the TOC's job, and a Clear that silently deleted someone's result would be
     a surprise. */
  const clearPanel = () => {
    setSummary(null);
    setInside([]);
    setError(null);
    setTruncatedAt(null);
  };

  return (
    <>
      <ToolPanelHeader
        title="Buffer"
        description="Draw a distance ring around a layer's features."
        onClose={ctx.onClose}
      />

      <Field label="Buffer this layer">
        <LayerSelect layers={allowed} value={layerId} onChange={setLayerId} />
      </Field>

      {/* Buffer every feature in the layer (the original behaviour), or just
          whichever ones are already selected — from the Select tool, Select
          By Location, a Query result, or the attribute table, all of which
          publish to the same shared selection. The same choice ClipPanel
          already offers for its own source layers. */}
      <div className="flex gap-1 rounded-md bg-slate-100 p-0.5 text-[11px] font-medium">
        <button
          type="button"
          onClick={() => setSourceMode('all')}
          className={`flex-1 rounded px-2 py-1 transition ${
            sourceMode === 'all' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          All features
        </button>
        <button
          type="button"
          onClick={() => setSourceMode('selected')}
          className={`flex-1 rounded px-2 py-1 transition ${
            sourceMode === 'selected' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          Selected features
        </button>
      </div>

      {/* Single ring is still the default tab — most buffers only ever need
          one distance, and the extra ring-list UI would be clutter for that
          common case. */}
      <div className="flex gap-1 rounded-md bg-slate-100 p-0.5 text-[11px] font-medium">
        <button
          type="button"
          onClick={() => setMode('single')}
          className={`flex-1 rounded px-2 py-1 transition ${
            mode === 'single' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          Single ring
        </button>
        <button
          type="button"
          onClick={() => setMode('multi')}
          className={`flex-1 rounded px-2 py-1 transition ${
            mode === 'multi' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          Multi-ring
        </button>
      </div>

      {mode === 'single' ? (
        <FieldRow>
          <Field label="Distance">
            <input
              type="number"
              min={0}
              value={distance}
              onChange={(event) => setDistance(event.target.value)}
              className={inputClass}
            />
          </Field>
          <Field label="Unit">
            <select value={unit} onChange={(event) => setUnit(event.target.value)} className={inputClass}>
              <option value="m">Metres</option>
              <option value="km">Kilometres</option>
              <option value="ft">Feet</option>
              <option value="mi">Miles</option>
            </select>
          </Field>
        </FieldRow>
      ) : (
        <>
          <Field label="Unit">
            <select value={unit} onChange={(event) => setUnit(event.target.value)} className={inputClass}>
              <option value="m">Metres</option>
              <option value="km">Kilometres</option>
              <option value="ft">Feet</option>
              <option value="mi">Miles</option>
            </select>
          </Field>
          <Field label="Ring distances">
            <div className="space-y-1">
              {ringDistances.map((value, index) => (
                <div key={index} className="flex items-center gap-1">
                  <input
                    type="number"
                    min={0}
                    value={value}
                    onChange={(event) => updateRingDistance(index, event.target.value)}
                    className={inputClass}
                    placeholder={`Ring ${index + 1}`}
                  />
                  <button
                    type="button"
                    onClick={() => removeRing(index)}
                    disabled={ringDistances.length <= 1}
                    className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-red-500 disabled:opacity-30"
                    aria-label="Remove ring"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={addRing}
                className="flex items-center gap-1 text-[11px] font-medium text-blue-600 hover:text-blue-700"
              >
                <Plus className="h-3.5 w-3.5" /> Add ring
              </button>
            </div>
          </Field>
        </>
      )}

      <ToolActions>
        <button
          type="button"
          onClick={() => void run()}
          disabled={!source || busy}
          className={`${btnClass} gl-btn--primary`}
        >
          {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          {busy ? 'Buffering…' : 'Buffer'}
        </button>
        <button
          type="button"
          onClick={clearPanel}
          disabled={!summary}
          className={`${btnClass} gl-btn--secondary`}
        >
          Reset
        </button>
      </ToolActions>

      {error && <Notice tone="warn">{error}</Notice>}

      {truncatedAt !== null && (
        <Notice tone="warn">
          Only the first {truncatedAt.toLocaleString()} features were buffered — the source layer (or the
          service behind it) has more than that. Narrow it with a filter, or zoom in and try again, to
          cover the rest.
        </Notice>
      )}

      {summary && (
        <>
          <Notice>
            {summary.mode === 'multi'
              ? `Built ${summary.ringCount} ring${summary.ringCount === 1 ? '' : 's'} out to ${summary.radiusM.toLocaleString(
                  undefined,
                  { maximumFractionDigits: 0 },
                )} m from ${summary.features.toLocaleString()} feature${summary.features === 1 ? '' : 's'}`
              : `Buffered ${summary.features.toLocaleString()} feature${summary.features === 1 ? '' : 's'} at ${summary.radiusM.toLocaleString(
                  undefined,
                  { maximumFractionDigits: 0 },
                )} m`}
            {summary.layerId
              ? ' — added to the Layers tab as a temporary layer. It stays until you remove it, and nobody else sees it.'
              : '.'}
          </Notice>
          {listResults &&
            (inside.length === 0 ? (
              <EmptyHint>Nothing from the other switched-on layers falls inside.</EmptyHint>
            ) : (
              <div className="space-y-1">
                <ResultHeader label="Inside the buffer" count={inside.reduce((sum, row) => sum + row.count, 0)} />
                {inside.map((row) => (
                  <ResultRow key={row.layer} title={row.layer} detail={`${row.count} feature${row.count === 1 ? '' : 's'}`} />
                ))}
              </div>
            ))}
        </>
      )}

      {/* Says plainly what the maths is, because a buffer that looks exact and
          is not would be the worst outcome here. */}
      <p className="text-[10.5px] leading-snug text-slate-400">
        {mode === 'multi'
          ? "Each ring is the true geometric buffer of every feature in the layer, unioned into one band per distance and cut so rings never overlap — points, lines and polygons all buffer the same way."
          : "The buffer is the true geometric offset of every feature in the layer, unioned into one seamless shape — points, lines and polygons all buffer the same way."}{' '}
        Containment checks against the other switched-on layers use distance to the nearest point
        on the source geometry, not to a centre point.
      </p>
    </>
  );
}
